<template>
  <ViewerLayoutRoot class="video-guest-layout" data-testid="video-guest-layout">
    <GuestTopBar />

    <!-- Split view: Logs mode or desktop Video mode with chapters -->
    <template v-if="splitConfig">
      <SplitViewContainer class="video-guest-layout__split" v-bind="splitConfig">
        <template #left>
          <div class="video-guest-layout__video-col">
            <div class="video-guest-layout__video">
              <ViewerContent />
            </div>
            <MetadataSection :show-toolbar="false" />
          </div>
        </template>
        <template #right>
          <div class="video-guest-layout__side-panel">
            <component :is="splitRightPanel" />
          </div>
        </template>
      </SplitViewContainer>
    </template>

    <!-- Stacked: mobile or no chapters -->
    <div v-else class="video-guest-layout__media-row" :class="{ 'video-guest-layout__media-row--single': !hasChapters }">
      <div class="video-guest-layout__video-col">
        <div class="video-guest-layout__video">
          <ViewerContent />
        </div>
        <MetadataSection :show-toolbar="false" />
      </div>
      <div v-if="hasChapters" class="video-guest-layout__side-panel">
        <GuestChaptersSidePanel />
      </div>
    </div>

    <EngagementBar />

    <div class="video-guest-layout__tabs">
      <VideoGuestTabs @addedComment="emit('addedComment')" />
    </div>
  </ViewerLayoutRoot>
</template>

<script setup lang="ts">
import { computed, watchEffect, type Component } from 'vue';
import { storeToRefs } from 'pinia';

import ViewerContent from '@/modules/viewer/components/Content.vue';
import GuestTopBar from '../../shared/GuestTopBar.vue';
import EngagementBar from '../../shared/EngagementBar.vue';
import MetadataSection from '../../shared/MetadataSection.vue';
import GuestChaptersSidePanel from './GuestChaptersSidePanel.vue';
import VideoGuestTabs from './VideoGuestTabs.vue';
import LogsSidePanel from '@/modules/viewer/components/logs/LogsSidePanel.vue';
import SplitViewContainer from '@/modules/viewer/components/SplitViewContainer.vue';

import { useAiMetadataStore } from '@/modules/viewer/store/useAiMetadataStore';
import { useViewerModeStore, ViewerMode } from '@/modules/viewer/store/useViewerModeStore';
import { useViewerAvailableModes } from '@/modules/viewer/composables/useViewerAvailableModes';
import { useViewerModeUrlSync } from '@/modules/viewer/composables/useViewerModeUrlSync';
import { useUser } from '@/modules/viewer/composables/useUser';

import ViewerLayoutRoot from '../../shared/ViewerLayoutRoot.vue';
const emit = defineEmits<{
  addedComment: [];
}>();

const { hasChapters } = storeToRefs(useAiMetadataStore());
const { isSameOrgAsItem: canUseLogs } = useUser();

const viewerModeStore = useViewerModeStore();
const { isLogsMode, currentMode } = storeToRefs(viewerModeStore);

const { isMobile } = useViewerAvailableModes();
const isDesktop = computed(() => !isMobile.value);
useViewerModeUrlSync();

const splitConfig = computed(() => {
  if (canUseLogs.value && isLogsMode.value) {
    return { defaultRatio: 0.5, minRatio: 0.25, maxRatio: 0.75, storageKey: 'viewer-logs-split-ratio' };
  }
  if (isDesktop.value && hasChapters.value) {
    return { defaultRatio: 0.7, minRatio: 0.4, maxRatio: 0.85, storageKey: 'viewer-video-split-ratio' };
  }
  return null;
});

const splitRightPanel = computed<Component>(() => {
  if (canUseLogs.value && isLogsMode.value) return LogsSidePanel;
  return GuestChaptersSidePanel;
});

// Defensive guard: if a viewer who can't use Logs somehow lands in LOGS mode
// (e.g. shared URL with ?mode=logs), snap them back to Video.
watchEffect(() => {
  if (!canUseLogs.value && currentMode.value === ViewerMode.LOGS) {
    viewerModeStore.setMode(ViewerMode.VIDEO);
  }
});
</script>

<style scoped>
.video-guest-layout__media-row {
  @apply flex flex-col gap-4;
}

@media (min-width: 1024px) {
  .video-guest-layout__media-row {
    display: grid;
    grid-template-columns: 1fr minmax(280px, 360px);
    align-items: stretch;
    gap: 1rem;
  }

  .video-guest-layout__media-row--single {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 1440px) {
  .video-guest-layout__media-row {
    grid-template-columns: 1fr 360px;
  }

  .video-guest-layout__media-row--single {
    grid-template-columns: 1fr;
  }
}

.video-guest-layout__video-col {
  @apply flex flex-col min-w-0;
}

.video-guest-layout__video {
  @apply min-w-0;
}

.video-guest-layout__video :deep(.content-wrapper) {
  background-color: transparent !important;
}

.video-guest-layout__side-panel {
  @apply flex flex-col w-full mt-2;
}

@media (min-width: 1024px) {
  .video-guest-layout__side-panel {
    @apply mt-0;
  }

  .video-guest-layout__side-panel :deep(.guest-chapters-panel) {
    @apply flex-grow;
    max-height: 100%;
    overflow-y: auto;
  }
}

.video-guest-layout__tabs {
  @apply mt-2;
}

/* Split view (Logs mode and Video mode with chapters) */
.video-guest-layout__split {
  min-height: 0;
}

.video-guest-layout__split :deep(.video-guest-layout__side-panel) {
  @apply mt-0 h-full;
}

.video-guest-layout__split :deep(.logs-panel) {
  @apply h-full;
}

.video-guest-layout__split :deep(.video-guest-layout__video-col) {
  @apply h-full;
}
</style>
